import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { usePage } from '../contexts/CmsContext';
import { getDeptIcon } from '../lib/deptIcons';
import {
  Building2,
  Stethoscope,
  Heart,
  Syringe,
  Clock,
  Ambulance,
  Users,
  Activity,
  ArrowRight,
  CheckCircle,
} from 'lucide-react';

// Fallback used only if CMS is unreachable. All real values come from
// /api/cms/pages/hospital and are editable via /admin/cms → Hospital Page.
const FALLBACK = {
  hero: {
    image: 'https://images.unsplash.com/photo-1586773860418-d37222d8fce3?w=1920&q=80',
    badge: 'Teaching Hospital',
    title: 'SSMCH Teaching Hospital',
    subtitle:
      'A multi-specialty teaching hospital providing comprehensive healthcare services while serving as a training ground for future medical professionals.',
  },
  stats: [
    { label: 'Bed Capacity', value: '930', suffix: 'Beds' },
    { label: 'ICU Beds', value: '---' },
    { label: 'Operation Theatres', value: '---' },
    { label: 'OPD Patients/Day', value: '---' },
  ],
  overview: [],
  overview_image: 'https://images.unsplash.com/photo-1579684288452-b334934f845f?w=800&q=80',
  medical_superintendent: null,
  departments: [],
  services: [],
  facilities: [],
  opd_timings: {
    morning: { label: 'Morning OPD', time: '9:00 AM - 1:00 PM' },
    evening: { label: 'Evening OPD', time: '4:00 PM - 6:00 PM' },
    emergency: { label: 'Emergency', time: '24 Hours' },
  },
};

// Map CMS icon name strings (for the Services block) to lucide-react components.
const ICON_MAP = {
  Ambulance,
  Activity,
  Syringe,
  Heart,
  Stethoscope,
  Building2,
  Users,
  Clock,
};

const SUPER_SPECIALTY = new Set([
  'Oncology / Cancer',
  'Neurology',
  'Cardiology',
  'Urology',
  'Paediatric Surgery',
]);

const departmentIcon = (name) => getDeptIcon(name);

const Hospital = () => {
  const { data: cms } = usePage('hospital');
  const hero = cms?.hero || FALLBACK.hero;
  const stats = Array.isArray(cms?.stats) && cms.stats.length ? cms.stats : FALLBACK.stats;
  const overview = Array.isArray(cms?.overview) && cms.overview.length ? cms.overview : FALLBACK.overview;
  const overviewImage = cms?.overview_image || FALLBACK.overview_image;
  const ms = cms?.medical_superintendent || FALLBACK.medical_superintendent;
  const departments = Array.isArray(cms?.departments) && cms.departments.length ? cms.departments : FALLBACK.departments;
  const services = Array.isArray(cms?.services) && cms.services.length ? cms.services : FALLBACK.services;
  const facilities = Array.isArray(cms?.facilities) && cms.facilities.length ? cms.facilities : FALLBACK.facilities;
  const opd = cms?.opd_timings || FALLBACK.opd_timings;

  const clinicalDepts = departments.filter((d) => !SUPER_SPECIALTY.has(d));
  const superDepts = departments.filter((d) => SUPER_SPECIALTY.has(d));

  return (
    <div className="page-transition" data-testid="hospital-page">
      {/* Hero Section */}
      <section
        className="relative py-20 md:py-32 hero-overlay"
        style={{
          backgroundImage: `url(${hero.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="hospital-hero"
      >
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="inline-block bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
              {hero.badge}
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white font-bold leading-tight mb-6">
              {hero.title}
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed">{hero.subtitle}</p>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-10 bg-white border-b border-gray-100" data-testid="hospital-stats">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`hospital-stat-${index}`}>
                <p className="font-heading text-3xl md:text-4xl font-bold text-[#4ECDC4]">
                  <span data-testid={`hospital-stat-${index}-value`}>{stat.value}</span>
                  {stat.suffix && (
                    <span className="text-lg font-normal text-gray-600 ml-1">{stat.suffix}</span>
                  )}
                </p>
                <p className="text-sm text-gray-600 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hospital Overview */}
      <section className="section-padding bg-[#F8F9FA]" data-testid="hospital-overview">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-[#4ECDC4]/10 text-[#4ECDC4] px-4 py-2 rounded-full text-sm font-medium mb-4">
                Overview
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C] mb-6">
                About Our Hospital
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                {overview.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            </div>
            <div>
              <img
                src={overviewImage}
                alt="Hospital Operation Theatre"
                className="rounded-2xl shadow-xl w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Medical Superintendent */}
      {ms && (
        <section className="section-padding bg-white" data-testid="medical-superintendent">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-10">
                <span className="inline-block bg-[#4ECDC4]/10 text-[#4ECDC4] px-4 py-2 rounded-full text-sm font-medium mb-4">
                  Hospital Leadership
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C]">
                  Medical Superintendent
                </h2>
              </div>
              <div className="flex flex-col md:flex-row items-center gap-8 bg-gradient-to-br from-[#F8F9FA] to-white rounded-2xl p-8 md:p-10 shadow-lg border border-gray-100">
                {ms.photo_url && (
                  <div className="flex-shrink-0">
                    <div className="w-48 h-60 rounded-xl overflow-hidden shadow-xl border-4 border-[#4ECDC4]/30">
                      <img
                        src={ms.photo_url}
                        alt={`${ms.name} - Medical Superintendent, SSMCH`}
                        className="w-full h-full object-cover object-top"
                        data-testid="ms-photo"
                      />
                    </div>
                  </div>
                )}
                <div className="text-center md:text-left">
                  {ms.name && (
                    <h3 className="font-heading text-2xl font-bold text-[#1A202C] mb-1">{ms.name}</h3>
                  )}
                  {ms.qualification && (
                    <p className="text-sm text-gray-500 mb-1">{ms.qualification}</p>
                  )}
                  {ms.designation && (
                    <p className="text-[#4ECDC4] font-semibold mb-4">{ms.designation}</p>
                  )}
                  {ms.expertise && <p className="text-gray-600 leading-relaxed mb-3">{ms.expertise}</p>}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-gray-500 mb-3">
                    {ms.publications && <p>{ms.publications}</p>}
                    {ms.conferences && <p>{ms.conferences}</p>}
                    {ms.achievements && <p>{ms.achievements}</p>}
                    {ms.date_of_joining && <p>At SSMCH since {ms.date_of_joining}</p>}
                  </div>
                  {ms.former_position && <p className="text-sm text-gray-500">{ms.former_position}</p>}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Departments */}
      {departments.length > 0 && (
        <section className="section-padding bg-[#F8F9FA]" data-testid="hospital-departments">
          <div className="container-custom">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-block bg-[#D4A84B]/10 text-[#D4A84B] px-4 py-2 rounded-full text-sm font-medium mb-4">
                Hospital Departments
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C] mb-4">
                Clinical & Super Specialty Departments
              </h2>
              <p className="text-gray-600 text-lg">
                Our hospital comprises {departments.length} clinical and super specialty departments providing comprehensive healthcare.
              </p>
            </div>

            {[
              { label: 'Clinical Departments', list: clinicalDepts },
              { label: 'Super Specialty Departments', list: superDepts },
            ].map(({ label, list }) =>
              list.length ? (
                <div key={label} className="mb-10">
                  <h3 className="font-heading text-xl font-bold text-[#1A202C] mb-4 border-l-4 border-[#4ECDC4] pl-3">
                    {label}
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {list.map((dept, index) => {
                      const Icon = departmentIcon(dept);
                      return (
                        <Card
                          key={index}
                          className="card-hover group border border-gray-100 shadow-none hover:shadow-md"
                          data-testid={`hosp-dept-${dept.toLowerCase().replace(/[^a-z]/g, '-')}`}
                        >
                          <CardContent className="p-5 text-center">
                            <div className="w-12 h-12 bg-[#4ECDC4]/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:bg-[#4ECDC4]/20 transition-colors">
                              <Icon className="text-[#4ECDC4]" size={22} />
                            </div>
                            <p className="text-sm font-medium text-[#1A202C]">{dept}</p>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ) : null
            )}
          </div>
        </section>
      )}

      {/* Patient Services */}
      {services.length > 0 && (
        <section className="section-padding bg-[#F8F9FA]" data-testid="patient-services">
          <div className="container-custom">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-block bg-[#9ACD32]/10 text-[#9ACD32] px-4 py-2 rounded-full text-sm font-medium mb-4">
                Patient Care
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C] mb-4">
                Patient Services
              </h2>
              <p className="text-gray-600 text-lg">
                Comprehensive healthcare services designed to provide quality patient care.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {services.map((service, index) => {
                const Icon = ICON_MAP[service.icon] || Stethoscope;
                return (
                  <Card
                    key={index}
                    className="card-hover group border-0 shadow-sm"
                    data-testid={`service-${index}`}
                  >
                    <CardContent className="p-8">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#4ECDC4]/20 to-[#4ECDC4]/5 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                        <Icon className="text-[#4ECDC4]" size={28} />
                      </div>
                      <h3 className="font-heading text-xl font-semibold text-[#1A202C] mb-3">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{service.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Facilities & OPD */}
      <section className="section-padding bg-white" data-testid="hospital-facilities">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <span className="inline-block bg-[#4ECDC4]/10 text-[#4ECDC4] px-4 py-2 rounded-full text-sm font-medium mb-4">
                Infrastructure
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C] mb-6">
                Hospital Facilities
              </h2>
              <ul className="space-y-4">
                {facilities.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="text-[#4ECDC4] flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <Card className="border-0 shadow-lg bg-gradient-to-br from-[#4ECDC4]/5 to-[#D4A84B]/5">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 bg-[#4ECDC4] rounded-full flex items-center justify-center">
                      <Clock className="text-white" size={28} />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl font-semibold text-[#1A202C]">OPD Timings</h3>
                      <p className="text-gray-600 text-sm">Outpatient Department Schedule</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {['morning', 'evening', 'emergency'].map((slot) => {
                      const entry = opd[slot];
                      if (!entry) return null;
                      const highlight = slot === 'emergency';
                      return (
                        <div
                          key={slot}
                          className="flex justify-between items-center p-4 bg-white rounded-lg"
                          data-testid={`opd-${slot}`}
                        >
                          <span className="font-medium text-gray-700">{entry.label}</span>
                          <span
                            className={`font-semibold ${
                              highlight ? 'text-[#D4A84B]' : 'text-[#4ECDC4]'
                            }`}
                          >
                            {entry.time}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1A202C]" data-testid="hospital-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Learn More About Clinical Training at SSMCH
          </h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Our teaching hospital provides extensive clinical exposure for medical students.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/academics">
              <Button
                className="bg-[#4ECDC4] hover:bg-[#3DBDB4] text-white rounded-full px-8 h-12 font-medium shadow-lg hover:shadow-xl transition-all"
                data-testid="hospital-academics-btn"
              >
                View Academic Program
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#1A202C] rounded-full px-8 h-12 font-medium transition-all"
                data-testid="hospital-contact-btn"
              >
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Hospital;
